export interface User {
  id: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  role: 'USER' | 'MANAGER' | 'ADMIN';
  avatar?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  managedTeams?: Team[];
  teamMemberships?: {
    team: Team;
  }[];
}

export interface Team {
  id: string;
  name: string;
  description?: string;
  color: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  manager: {
    id: string;
    firstName: string;
    lastName: string;
    username: string;
    email: string;
  };
  members: {
    id: string;
    user: {
      id: string;
      firstName: string;
      lastName: string;
      username: string;
      email: string;
      avatar?: string;
    };
  }[];
  _count?: {
    tasks: number;
    projects: number;
  };
  projects?: {
    project: {
      id: string;
      name: string;
      description?: string;
      status: string;
      priority: string;
      color: string;
      creator: {
        id: string;
        firstName: string;
        lastName: string;
        email: string;
      };
      _count: {
        tasks: number;
      };
    };
  }[];
}

export interface Project {
  id: string;
  name: string;
  description?: string;
  status: string;
  priority: string;
  startDate?: string;
  endDate?: string;
  dueDate?: string;
  budget?: number;
  color: string;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  creator: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  teams?: {
    team: {
      id: string;
      name: string;
      color: string;
    };
  }[];
  tasks?: Task[];
  _count?: {
    tasks: number;
  };
}

export interface Task {
  id: string;
  title: string;
  description?: string;
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  status: 'TODO' | 'IN_PROGRESS' | 'REVIEW' | 'DONE' | 'CANCELLED';
  startDate?: string;
  endDate?: string;
  dueDate?: string;
  estimatedHours?: number;
  actualHours?: number;
  progressPercentage?: number;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  // Recurring task fields
  recurringTemplateId?: string;
  recurringTemplate?: RecurringTaskTemplate;
  isRecurringInstance: boolean;
  team?: {
    id: string;
    name: string;
    color: string;
  };
  project?: {
    id: string;
    name: string;
    color: string;
  };
  creator: {
    id: string;
    firstName: string;
    lastName: string;
    username: string;
  };
  assignees: {
    id: string;
    user: {
      id: string;
      firstName: string;
      lastName: string;
      username: string;
      avatar?: string;
    };
  }[];
  comments?: TaskComment[];
  tagRelations?: {
    tag: TaskTag;
  }[];
  feedbacks?: TaskFeedback[];
  finishDetails?: TaskFinishDetail[];
  _count?: {
    comments: number;
    feedbacks?: number;
    finishDetails?: number;
  };
}

export interface TaskComment {
  id: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    username: string;
    avatar?: string;
  };
}

export interface TaskTag {
  id: string;
  name: string;
  color: string;
}

export interface TaskFeedback {
  id: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  taskId: string;
  userId: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    username: string;
    role: 'USER' | 'MANAGER' | 'ADMIN';
  };
  task?: {
    id: string;
    title: string;
  };
}

export interface TaskFinishDetail {
  id: string;
  description: string;
  notes?: string;
  completedAt: string;
  createdAt: string;
  updatedAt: string;
  taskId: string;
  userId: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    username: string;
  };
  task?: {
    id: string;
    title: string;
  };
}

export interface CreateTaskFeedbackDto {
  content: string;
  taskId: string;
  userId: string;
}

export interface CreateTaskFinishDetailDto {
  description: string;
  notes?: string;
  completedAt?: string;
  taskId: string;
  userId: string;
}

// Recurring Task Interfaces
export type RecurrenceType = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';

export interface RecurringTaskTemplate {
  id: string;
  title: string;
  description?: string;
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  status: 'TODO' | 'IN_PROGRESS' | 'REVIEW' | 'DONE' | 'CANCELLED';
  estimatedHours?: number;

  // Recurring pattern configuration
  recurrenceType: RecurrenceType;
  recurrenceValue: number;
  daysOfWeek?: number[]; // Array of day numbers: 0=Sunday, 1=Monday, etc.
  dayOfMonth?: number; // For monthly patterns
  endDate?: string; // When to stop generating tasks
  maxOccurrences?: number; // Maximum number of tasks to generate

  // Template settings
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  lastGenerated?: string; // Last time tasks were generated

  // Relations
  teamId?: string;
  team?: {
    id: string;
    name: string;
    color: string;
  };
  projectId?: string;
  project?: {
    id: string;
    name: string;
    color: string;
  };
  creatorId: string;
  creator: {
    id: string;
    firstName: string;
    lastName: string;
    username: string;
  };

  // Generated tasks
  tasks?: Task[];
}

export interface CreateRecurringTaskDto {
  title: string;
  description?: string;
  priority?: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  status?: 'TODO' | 'IN_PROGRESS' | 'REVIEW' | 'DONE' | 'CANCELLED';
  estimatedHours?: number;
  teamId?: string;
  projectId?: string;
  creatorId: string;
  assigneeIds?: string[];
  tagIds?: string[];

  // Recurring pattern configuration
  recurrenceType: RecurrenceType;
  recurrenceValue?: number;
  daysOfWeek?: number[]; // Array of day numbers: 0=Sunday, 1=Monday, etc.
  dayOfMonth?: number; // For monthly patterns
  startDate?: string; // When to start generating tasks
  endDate?: string; // When to stop generating tasks
  maxOccurrences?: number; // Maximum number of tasks to generate
  isActive?: boolean;
}

export interface CreateUserDto {
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  password: string;
  role?: 'USER' | 'MANAGER' | 'ADMIN';
  avatar?: string;
}

export interface CreateTeamDto {
  name: string;
  description?: string;
  color?: string;
  managerId: string;
  memberIds?: string[];
}

// Learning Types
export interface LearningSection {
  id: string;
  name: string;
  description?: string;
  teamId: string;
  team: {
    id: string;
    name: string;
    color: string;
  };
  videos?: LearningVideo[]; // Made optional for backward compatibility
  content?: LearningContent[]; // New content property
  _count: {
    videos?: number;
    content?: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface LearningVideo {
  id: string;
  title: string;
  url: string;
  sectionId: string;
  section?: {
    id: string;
    name: string;
    team: {
      id: string;
      name: string;
      color: string;
    };
  };
  createdAt: string;
  updatedAt: string;
}

export enum LearningContentType {
  VIDEO = 'VIDEO',
  PDF = 'PDF',
  IMAGE = 'IMAGE',
}

export interface LearningContent {
  id: string;
  title: string;
  contentType: LearningContentType;
  url?: string; // For YouTube videos
  fileUrl?: string; // For uploaded files
  fileName?: string;
  fileSize?: number;
  sectionId: string;
  section?: {
    id: string;
    name: string;
    team: {
      id: string;
      name: string;
      color: string;
    };
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateLearningSectionDto {
  name: string;
  description?: string;
  teamId: string;
}

export interface UpdateLearningSectionDto {
  name?: string;
  description?: string;
  teamId?: string;
}

export interface CreateLearningVideoDto {
  title: string;
  url: string;
  sectionId: string;
}

export interface UpdateLearningVideoDto {
  title?: string;
  url?: string;
  sectionId?: string;
}

export interface CreateTaskDto {
  title: string;
  description?: string;
  priority?: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  status?: 'TODO' | 'IN_PROGRESS' | 'REVIEW' | 'DONE' | 'CANCELLED';
  startDate?: string;
  endDate?: string;
  dueDate?: string;
  estimatedHours?: number;
  teamId?: string;
  creatorId: string;
  assigneeIds?: string[];
  tagIds?: string[];

  // Recurring task options
  isRecurring?: boolean;
  recurringTaskData?: CreateRecurringTaskDto;
}

export interface AuthUser {
  id: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  role: 'USER' | 'MANAGER' | 'ADMIN';
  avatar?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: Task;
  color?: string;
}
